import React, { useState } from 'react'
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Link,
    InputAdornment,
    useTheme,
    CircularProgress
} from '@mui/material'
import {
    Email as EmailIcon,
    ArrowBack as ArrowBackIcon,
    LockReset as LockResetIcon
} from '@mui/icons-material'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { authApi } from '../../api/authApi.js'
import toast from 'react-hot-toast'
import { useLoading } from '../../hooks'

const ForgotPassword = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const userType = location.state?.userType || 'org'
    const { loading, withLoading } = useLoading()
    const [error, setError] = useState('')
    const theme = useTheme()

    const emailSchema = yup.object({
        email: yup
            .string()
            .email('Invalid email address')
            .required('Email is required'),
    })

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(emailSchema),
    })

    const handleSendOTP = async (data) => {
        setError('')
        withLoading(async () => {
            try {
                if (userType === 'employee') {
                    const response = await authApi.employeeForgotPassword(data)
                    if (response.data.status === 'success') {
                        toast.success(response.data.detail || 'A new password has been sent to your email address.')
                        navigate('/login')
                    } else {
                        setError(response.data.detail || 'Failed to send password.')
                        toast.error(response.data.detail || 'Failed to send password.')
                    }
                } else {
                    const response = await authApi.resetPassword(data)

                    if (response.data.status === 'success' || response.data.detail) {
                        toast.success(response.data.detail || 'A new password has been sent to your email address.')
                        navigate('/org/login')
                    } else {
                        setError(response.data.detail || 'Failed to send OTP')
                        toast.error(response.data.detail || 'Failed to send OTP')
                    }
                }
            } catch (err) {
                const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Failed to process request'
                setError(errorMessage)
                toast.error(errorMessage)
            }
        })
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'dark'
                    ? 'radial-gradient(circle at 50% 50%, #1e293b 0%, #0f172a 100%)'
                    : 'linear-gradient(135deg, #f0f9ff 0%, #cbebff 100%)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Abstract Background Shapes */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '-15%',
                    left: '-5%',
                    width: '500px',
                    height: '500px',
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, rgba(0,0,0,0) 70%)'
                        : 'radial-gradient(circle, rgba(239, 68, 68, 0.1) 0%, rgba(255,255,255,0) 70%)',
                    zIndex: 0,
                }}
            />

            <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
                <Paper
                    elevation={theme.palette.mode === 'dark' ? 4 : 0}
                    className="fade-in"
                    sx={{
                        p: { xs: 3, sm: 5 },
                        width: '100%',
                        borderRadius: 4,
                        background: theme.palette.mode === 'dark'
                            ? 'rgba(30, 41, 59, 0.7)'
                            : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
                        boxShadow: theme.palette.mode === 'light'
                            ? '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                            : 'none',
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                mx: 'auto',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <LockResetIcon sx={{ color: '#fff', fontSize: 32 }} />
                        </Box>
                        <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Forgot Password?
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Enter your email address to receive a new password
                        </Typography>
                    </Box>

                    {/* Error Alert is also handled by toast, but keeping for inline visibility if needed */}
                    {/* {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 3 }}
                            onClose={() => setError('')}
                        >
                            {error}
                        </Alert>
                    )} */}

                    <Box component="form" onSubmit={handleSubmit(handleSendOTP)}>
                        <TextField
                            margin="normal"
                            fullWidth
                            id="email"
                            label="Email Address"
                            autoComplete="email"
                            autoFocus
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                mt: 1,
                                mb: 2,
                                py: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`, // Use Error color for critical action
                                borderRadius: 2,
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Password'}
                        </Button>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            component={RouterLink}
                            to={userType === 'employee' ? '/login' : '/org/login'}
                            sx={{ textTransform: 'none', fontWeight: 500 }}
                        >
                            Back to Login
                        </Button>
                    </Box>

                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            © {new Date().getFullYear()} Face Recognition System. All rights reserved.
                        </Typography>
                    </Box>
                </Paper>
            </Container >
        </Box >
    )
}

export default ForgotPassword
